/**
 * Stock Validation Utilities
 * Provides functions for validating stock availability and formatting stock messages
 */

/**
 * Check if a product has sufficient stock for requested quantity
 * @param {Object} product - Product object with stock field
 * @param {number} requestedQuantity - Quantity user wants to add/purchase
 * @returns {Object} { valid: boolean, available: number, message: string }
 */
export const checkStockAvailability = (product, requestedQuantity) => {
  const availableStock = product?.stock || 0;
  const moq = product?.moq || 100;

  if (availableStock === 0) {
    return {
      valid: false,
      available: 0,
      message: `${product.name} is out of stock`,
      severity: 'error'
    };
  }

  if (requestedQuantity > availableStock) {
    return {
      valid: false,
      available: availableStock,
      message: `Only ${availableStock} units available for ${product.name}`,
      severity: 'warning'
    };
  }

  if (requestedQuantity < moq) {
    return {
      valid: false,
      available: availableStock,
      message: `Minimum order quantity for ${product.name} is ${moq} units`,
      severity: 'info'
    };
  }

  return {
    valid: true,
    available: availableStock,
    message: 'Stock available',
    severity: 'success'
  };
};

/**
 * Validate entire cart against current stock levels
 * @param {Array} cartItems - Array of cart items with { product, quantity }
 * @param {Array} products - Array of all products with current stock
 * @returns {Object} { valid: boolean, issues: Array<{ productId, productName, requested, available }> }
 */
export const validateCartStock = (cartItems, products) => {
  const issues = [];

  cartItems.forEach(cartItem => {
    // Find product in products array
    const productId = cartItem.product?._id || cartItem.product;
    const product = products.find(p => p._id === productId || p.id === productId);

    if (!product) {
      issues.push({
        productId,
        productName: cartItem.product?.name || 'Unknown Product',
        requested: cartItem.quantity,
        available: 0,
        message: 'Product not found',
        severity: 'error'
      });
      return;
    }

    const validation = checkStockAvailability(product, cartItem.quantity);

    if (!validation.valid) {
      issues.push({
        productId: product._id || product.id,
        productName: product.name,
        requested: cartItem.quantity,
        available: validation.available,
        message: validation.message,
        severity: validation.severity
      });
    }
  });

  return {
    valid: issues.length === 0,
    issues
  };
};

/**
 * Format stock status message for display
 * @param {number} stock - Available stock quantity
 * @param {number} threshold - Low stock threshold (default 100)
 * @returns {Object} { text: string, className: string, severity: string, color: string }
 */
export const formatStockStatus = (stock, threshold = 100) => {
  if (stock === 0) {
    return {
      text: 'Out of Stock',
      className: 'stock-out',
      severity: 'error',
      color: '#f44336'
    };
  }

  if (stock < threshold) {
    return {
      text: `Low Stock - ${stock.toLocaleString()} units left`,
      className: 'stock-low',
      severity: 'warning',
      color: '#ff9800'
    };
  }

  if (stock < 1000) {
    return {
      text: `In Stock - ${stock.toLocaleString()} units available`,
      className: 'stock-medium',
      severity: 'success',
      color: '#4caf50'
    };
  }

  return {
    text: `In Stock - ${stock.toLocaleString()} units available`,
    className: 'stock-high',
    severity: 'success',
    color: '#4caf50'
  };
};

/**
 * Calculate if quantity can be incremented
 * @param {number} currentQuantity - Current quantity in cart
 * @param {number} availableStock - Available stock
 * @param {number} moq - Minimum order quantity
 * @returns {boolean}
 */
export const canIncrementQuantity = (currentQuantity, availableStock, moq = 100) => {
  const nextQuantity = currentQuantity + moq;
  return nextQuantity <= availableStock;
};

/**
 * Calculate if quantity can be decremented
 * @param {number} currentQuantity - Current quantity in cart
 * @param {number} moq - Minimum order quantity
 * @returns {boolean}
 */
export const canDecrementQuantity = (currentQuantity, moq = 100) => {
  const nextQuantity = currentQuantity - moq;
  return nextQuantity >= moq;
};

/**
 * Get maximum quantity that can be added to cart
 * @param {number} availableStock - Available stock
 * @param {number} currentQuantity - Current quantity in cart
 * @param {number} moq - Minimum order quantity
 * @returns {number}
 */
export const getMaxAddableQuantity = (availableStock, currentQuantity = 0, moq = 100) => {
  const remainingStock = availableStock - currentQuantity;

  // Round down to nearest MOQ
  return Math.floor(remainingStock / moq) * moq;
};

/**
 * Check if product should show low stock warning
 * @param {number} stock - Available stock
 * @param {number} moq - Minimum order quantity
 * @returns {boolean}
 */
export const shouldShowLowStockWarning = (stock, moq = 100) => {
  return stock > 0 && stock < moq * 5; // Less than 5x MOQ
};

/**
 * Format stock history entry for display
 * @param {Object} historyEntry - Stock history entry
 * @returns {string}
 */
export const formatStockHistoryEntry = (historyEntry) => {
  const { operation, quantity, reason, timestamp } = historyEntry;
  const date = new Date(timestamp).toLocaleDateString();
  const operationText = operation === 'add' ? 'Added' : operation === 'subtract' ? 'Deducted' : 'Set to';

  return `${operationText} ${quantity} units - ${reason} (${date})`;
};
